import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useMediaQuery,
  FormHelperText,
  Grid,
  CircularProgress,
} from '@mui/material';
import Swal from 'sweetalert2';
import { useTheme } from '@mui/styles';
import { BROADCAST_MESSAGE_POPUP } from 'constant/app.config';
import { postApiCall } from 'api/apiService';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import { poppins400, poppins500 } from 'theme/common.style';
import Loading from 'assets/images/loading.png';

const BroadcastMessagePopup = ({
  open,
  onClose,
  onSend,
  buyerRegisterCheckBoxArr,
  tableData,
  setBuyerRegisterCheckBox,
  address,
}) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const [emailMessage, setEmailMessage] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false); // Loader state

  const [selectedBuyers, setSelectedBuyers] = useState(
    buyerRegisterCheckBoxArr?.map((buyer) => ({
      id:
        buyer?.checklistBuyerId ||
        buyer?.buyerDetails?.user?.userId ||
        'Unknown',
      name: buyer?.buyerDetails?.user?.userName || 'Unknown',
      SurName: buyer?.buyerDetails?.user?.userSurName || 'Unknown',
    })) || []
  );

  const handleRemoveBuyer = (id) => {
    if (selectedBuyers.length > 2) {
      setSelectedBuyers((prev) => prev.filter((buyer) => buyer.id !== id));
    }
  };

  const handleSend = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (!emailMessage.trim()) {
      setError(true);
      return;
    }

    setError(false);
    setLoading(true); // Start loader

    const buyerIds = selectedBuyers.map((buyer) => buyer.id);

    const propertyId =
      tableData?.PropertyDetails?.property?.propertyId ||
      tableData?.PropertyDetails?.property?.propertyId; // Ensure propertyId is assigned

    if (!propertyId) {
      setLoading(false);
      return;
    }

    const payload = {
      propertyId,
      message: emailMessage,
      buyerId: buyerIds,
    };

    try {
      const response = await postApiCall(BROADCAST_MESSAGE_POPUP, payload);
      if (response.data.status === 1) {
        Swal.fire({
          title: response?.data?.heading,
          text: response?.data?.message,
          icon: 'success',
          confirmButtonText: 'Close',
        }).then(() => {
          onClose();
        });
        setBuyerRegisterCheckBox([]);
        onSend(emailMessage);
        setEmailMessage('');
        onClose();
      } else {
        Swal.fire({
          title: 'Error',
          text:
            response?.data?.message ||
            'Something went wrong. Please try again.',
          icon: 'error',
          confirmButtonText: 'Close',
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          onClose();
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text:
          error?.response?.data?.message ||
          error?.message ||
          'An unexpected error occurred. Please try again later.',
        icon: 'error',
        confirmButtonText: 'Close',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        onClose();
      });
    } finally {
      setLoading(false); // Stop loader
    }
  };

  useEffect(() => {
    if (loading) {
      document.body.style.pointerEvents = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [loading]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-container .MuiPaper-root': {
          width: matchessm ? '100vw' : '886px',
          borderRadius: '5px',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '5px',
          minWidth: matchessm ? '100%' : '400px',
          boxShadow: '-4px, 4px rgba(0, 0, 0, 0.09)',
          padding: matchessm ? '20px 10px' : '4px',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Grid container spacing={2} sx={{ padding: '15px' }}>
          <Grid item xs={12}>
            <Typography
              sx={{
                ...poppins400,
                fontSize: matchessm ? '10px' : '14px',
                color: '#666666',
              }}
            >
              {address}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            padding="8px 0px 10px 0"
          >
            <Pendingwarning sx={{ fontSize: '18px' }} />
            <Typography
              sx={{
                ...poppins500,
                fontSize: matchessm ? '16px' : '24px',
                paddingLeft: '12px',
              }}
            >
              Broadcast Message
            </Typography>
          </Grid>
        </Grid>

        <DialogContent
          sx={{
            fontFamily: 'Poppins',
            padding: matchessm ? '10px' : '20px',
            marginTop: -3.5,
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: 'Poppins' }}>
            To
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              padding: '5px',
              minHeight: '40px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              alignItems: 'center',
              marginTop: 1,
            }}
          >
            {selectedBuyers.length > 0 ? (
              selectedBuyers.map((buyer) => (
                <Chip
                  key={buyer.id}
                  label={`${buyer.name} ${buyer.SurName}`}
                  onDelete={
                    selectedBuyers.length > 2
                      ? () => handleRemoveBuyer(buyer.id)
                      : undefined
                  }
                  sx={{ fontFamily: 'Poppins', fontWeight: 300 }}
                />
              ))
            ) : (
              <Typography sx={{ color: '#999', fontFamily: 'Poppins' }}>
                Select buyers...
              </Typography>
            )}
          </Box>

          <Typography variant="h6" sx={{ marginTop: 2, fontFamily: 'Poppins' }}>
            Message
          </Typography>
          <TextField
            sx={{
              width: '100%',
              marginTop: 1,
              borderRadius: '5px',
              '& .MuiInputLabel-root': {
                fontFamily: 'Poppins',
                fontWeight: 300,
              },
              '& .MuiInputBase-input': {
                fontFamily: 'Poppins',
                fontWeight: 300,
              },
            }}
            placeholder="Text Message"
            type="text"
            multiline
            rows={4}
            fullWidth
            value={emailMessage}
            onChange={(e) => {
              const input = e.target.value;
              const sentenceCase = input
                .toLowerCase()
                .replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
              setEmailMessage(sentenceCase);
              if (error) setError(false);
            }}
            error={error}
          />
          {error && (
            <FormHelperText
              sx={{ color: '#8B0000', fontSize: '14px', fontFamily: 'Poppins' }}
            >
              Message cannot be empty!
            </FormHelperText>
          )}
        </DialogContent>

        <Box
          sx={{
            padding: '10px 20px',
            display: 'flex',
            flexDirection: matchessm ? 'column' : 'row',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: -2,
          }}
        >
          <Button
            sx={{
              padding: '8px 12px',
              borderRadius: '5px',
              fontSize: '12px',
              color: '#666',
              fontFamily: 'Poppins',
              textTransform: 'capitalize',
              border: '1px solid #1123471A',
              minWidth: matchessm ? '100%' : 100,
              '&:hover': { background: 'lightgrey' },
            }}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            sx={{
              padding: '8px 12px',
              borderRadius: '5px',
              fontSize: '12px',
              background: '#4CA4D6',
              color: '#FFF',
              fontFamily: 'Poppins',
              textTransform: 'capitalize',
              minWidth: matchessm ? '100%' : 100,
              '&:hover': { background: '#4CA4D6' },
            }}
            onClick={handleSend}
            disabled={loading}
          >
            Send
          </Button>
          {loading ? (
            <img
              alt="Logo"
              src={Loading}
              className="loaderImage"
              style={{ height: '40px', width: '40px' }}
            />
          ) : (
            ''
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default BroadcastMessagePopup;
